import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export type BubbleSide = "agent" | "user";

/**
 * The one bubble shape of every conversation we draw: a committed message, the
 * streaming answer, the typing dots, and the onboarding's questions and answers.
 * They used to each carry their own copy of the geometry, and drifted — the typing
 * bubble had a sharper tail than the message that replaced it.
 *
 * The **tail** is the corner on the speaker's side, a quarter of the tenant's radius.
 * It used to be a fixed `rounded-*-md`, and once that token grew to 10px a tenant who
 * picked "Léger" (8px) got a tail rounder than the rest of the bubble — the one corner
 * meant to be sharp was the softest.
 *
 * `compact` is the back-office call review: the team reads a whole call at once, so
 * the bubbles lose their padding. It keeps the app's own radii, not the tenant's —
 * the team is not the visitor.
 */
export function Bubble({
  side,
  density = "chat",
  tone = "fill",
  className,
  children,
}: {
  side: BubbleSide;
  density?: "chat" | "compact";
  /**
   * `plain` is a surface for real controls (the onboarding's answer blocks): a form on
   * a saturated fill is unreadable, so it takes the card instead, bordered.
   */
  tone?: "fill" | "plain";
  className?: string;
  children: ReactNode;
}) {
  const compact = density === "compact";
  return (
    <div
      className={cn(
        compact
          ? "max-w-[75%] rounded-lg px-3 py-1.5"
          : "max-w-[85%] rounded-[var(--chat-radius,1rem)] px-4 py-3",
        side === "user"
          ? compact
            ? "rounded-br-xs"
            : "rounded-br-[calc(var(--chat-radius,1rem)/4)]"
          : compact
            ? "rounded-bl-xs"
            : "rounded-bl-[calc(var(--chat-radius,1rem)/4)]",
        tone === "plain"
          ? "border bg-card text-card-foreground"
          : side === "user"
            ? "bg-primary text-primary-foreground"
            : "bg-muted",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A bubble in its row: pushed to its speaker's side, the agent's with a face beside
 * it, level with the bubble's last line (where a reply is read from).
 */
export function BubbleRow({
  side,
  avatar,
  className,
  children,
}: {
  side: BubbleSide;
  avatar?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-end gap-2",
        side === "user" ? "justify-end" : "justify-start",
        className,
      )}
    >
      {side === "agent" && avatar}
      {children}
    </div>
  );
}

/** "The agent is typing": three dots, for the inside of an agent bubble. */
export function TypingDots() {
  return (
    <div className="flex h-5 items-center gap-1">
      {["0ms", "150ms", "300ms"].map((delay) => (
        <span
          key={delay}
          className="size-2 animate-bounce rounded-full bg-muted-foreground/50"
          style={{ animationDelay: delay, animationDuration: "1s" }}
        />
      ))}
    </div>
  );
}
